import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { downloadFromUrl } from '../../services/products_api'
import { getDownloadStatus } from '../../services/downloadsApi'
import { extractApiError } from './utils'

export interface DownloadItem {
  itemId: number
  itemName: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error?: string
}

export type DownloadStatus =
  | 'idle'
  | 'pending'
  | 'preparing'
  | 'generating'
  | 'downloading'
  | 'completed'
  | 'failed'

/**
 * Common status response type that all getStatus functions must return.
 * Compatible with DownloadStatusResponse and legacy status endpoints.
 */
export interface StatusResponse {
  request_id: number
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  download_url?: string
  detail?: string
}

/**
 * Configuration for starting a download via the download widget.
 * Callers provide their own initiation and status-polling functions,
 * making the widget source-agnostic.
 */
export interface DownloadConfig {
  /** Items being downloaded (for display in the widget) */
  items: Array<{ id: number; name: string }>
  /**
   * Function that initiates the download.
   * Returns either a blob (synchronous) or a request ID (async polling).
   */
  initiate: (
    signal: AbortSignal
  ) => Promise<
    | { type: 'blob'; blob: Blob; filename: string }
    | { type: 'async'; requestId: number }
  >
  /** Status polling function. Defaults to the generic downloads endpoint. */
  getStatus?: (requestId: number) => Promise<StatusResponse>
  /** Called when download starts (e.g., to close a dropdown or modal) */
  onStart?: () => void
  /** Label for the download type (e.g., "spec sheets", "documents") */
  label?: string
}

interface DownloadState {
  status: DownloadStatus
  items: DownloadItem[]
  progress: number // 0-100
  totalCount: number
  completedCount: number
  failedCount: number
  errorMessage?: string
  label?: string
  requestId?: number // For async downloads
}

interface DownloadContextType {
  state: DownloadState
  startDownload: (config: DownloadConfig) => Promise<void>
  cancelDownload: () => void
  clearDownload: () => void
  isVisible: boolean
}

const DownloadContext = createContext<DownloadContextType | undefined>(
  undefined
)

const initialState: DownloadState = {
  status: 'idle',
  items: [],
  progress: 0,
  totalCount: 0,
  completedCount: 0,
  failedCount: 0,
}

/** Polling interval in milliseconds */
const POLLING_INTERVAL = 1000

export function DownloadProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DownloadState>(initialState)
  const [abortController, setAbortController] =
    useState<AbortController | null>(null)
  // Track polling state - requestId, items, and getStatus for the current async download
  const [pollingData, setPollingData] = useState<{
    requestId: number
    items: DownloadItem[]
    getStatus: (requestId: number) => Promise<StatusResponse>
    label?: string
  } | null>(null)
  // Ref to track if download has been triggered (prevents duplicate downloads)
  const downloadTriggeredRef = useRef(false)

  const isVisible =
    state.status !== 'idle' ||
    state.items.length > 0 ||
    state.completedCount > 0

  /**
   * Poll for async download status using React Query
   * - refetchInterval ensures sequential polling (won't fire while request is in-flight)
   * - Returns false to stop polling when completed/failed/expired
   */
  const { data: statusResponse } = useQuery<StatusResponse>({
    queryKey: ['download-status', pollingData?.requestId],
    queryFn: () => pollingData!.getStatus(pollingData!.requestId),
    enabled: !!pollingData?.requestId && !downloadTriggeredRef.current,
    refetchInterval: (query) => {
      const data = query.state.data
      // Stop polling if completed, failed, or expired
      if (
        data?.status === 'completed' ||
        data?.status === 'failed' ||
        data?.status === 'expired'
      ) {
        return false
      }
      return POLLING_INTERVAL
    },
  })

  /**
   * Handle status response changes from polling
   */
  useEffect(() => {
    if (!statusResponse || !pollingData) return
    if (downloadTriggeredRef.current) return

    // Handle pending status (Celery task hasn't started yet)
    if (statusResponse.status === 'pending') {
      setState((prev) => ({
        ...prev,
        status: 'pending',
        progress: 0,
      }))
      return
    }

    // Handle processing status
    if (statusResponse.status === 'processing') {
      setState((prev) => ({
        ...prev,
        status: 'generating',
        progress: statusResponse.progress_percentage,
      }))
      return
    }

    // Handle completed status
    if (statusResponse.status === 'completed' && statusResponse.download_url) {
      downloadTriggeredRef.current = true
      const handleDownload = async () => {
        setState((prev) => ({
          ...prev,
          status: 'downloading',
        }))

        try {
          // Build a default filename from the label (e.g. "Hospitals 2026-03-27.csv")
          const ext = statusResponse.download_url!.endsWith('.xlsx')
            ? '.xlsx'
            : '.csv'
          const date = new Date().toISOString().split('T')[0]
          const defaultFilename = pollingData.label
            ? `${pollingData.label} ${date}${ext}`
            : undefined

          const result = await downloadFromUrl(
            statusResponse.download_url!,
            abortController?.signal ?? new AbortController().signal,
            (progress) => {
              setState((prev) => ({
                ...prev,
                progress: Math.min(progress, 99),
              }))
            },
            defaultFilename
          )

          // Trigger file download
          const url = window.URL.createObjectURL(result.blob)
          const link = document.createElement('a')
          link.href = url
          link.download = result.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          setState((prev) => ({
            ...prev,
            status: 'completed',
            progress: 100,
            completedCount: prev.totalCount,
            items: pollingData.items.map((item) => ({
              ...item,
              status: 'completed',
            })),
          }))
        } catch (error) {
          if (abortController?.signal.aborted) return

          setState((prev) => ({
            ...prev,
            status: 'failed',
            errorMessage: 'Failed to download file',
            progress: 100,
          }))
        } finally {
          setPollingData(null)
          setAbortController(null)
        }
      }

      handleDownload()
    }

    // Handle failed/expired status
    if (
      statusResponse.status === 'failed' ||
      statusResponse.status === 'expired'
    ) {
      setState((prev) => ({
        ...prev,
        status: 'failed',
        errorMessage:
          statusResponse.detail ||
          (statusResponse.status === 'expired'
            ? 'Download link expired'
            : 'Download failed'),
        progress: 100,
        items: pollingData.items.map((item) => ({
          ...item,
          status: 'failed',
          error: statusResponse.detail,
        })),
      }))
      setPollingData(null)
    }
  }, [statusResponse, pollingData, abortController])

  const startDownload = useCallback(async (config: DownloadConfig) => {
    // Reset duplicate download prevention for new download
    downloadTriggeredRef.current = false

    // Create abort controller for cancellation
    const controller = new AbortController()
    setAbortController(controller)

    // Call onStart to close caller UI (dropdown/modal)
    config.onStart?.()

    // Initialize items
    const items: DownloadItem[] = config.items.map((item) => ({
      itemId: item.id,
      itemName: item.name,
      status: 'pending',
    }))

    setState({
      status: 'preparing',
      items,
      progress: 0,
      totalCount: config.items.length,
      completedCount: 0,
      failedCount: 0,
      label: config.label,
    })

    try {
      setState((prev) => ({
        ...prev,
        status: 'generating',
      }))

      const response = await config.initiate(controller.signal)

      if (response.type === 'blob') {
        // Synchronous blob response - trigger file download directly
        const url = window.URL.createObjectURL(response.blob)
        const link = document.createElement('a')
        link.href = url
        link.download = response.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        setState((prev) => ({
          ...prev,
          status: 'completed',
          progress: 100,
          completedCount: prev.totalCount,
          items: prev.items.map((item) => ({
            ...item,
            status: 'completed',
          })),
        }))
        setAbortController(null)
      } else if (response.type === 'async') {
        // Async response - start polling via React Query

        const statusFn = config.getStatus ?? getDownloadStatus
        setState((prev) => ({
          ...prev,
          requestId: response.requestId,
        }))
        setPollingData({
          requestId: response.requestId,
          items,
          getStatus: statusFn,
          label: config.label,
        })
      }
    } catch (error: unknown) {
      if (controller.signal.aborted) {
        setState((prev) => ({
          ...prev,
          status: 'idle',
          items: [],
          progress: 0,
        }))
        return
      }

      const errorMessage = await extractApiError(error)

      setState((prev) => ({
        ...prev,
        status: 'failed',
        errorMessage,
        progress: 100,
        items: prev.items.map((item) => ({
          ...item,
          status: item.status === 'pending' ? 'failed' : item.status,
          error: item.status === 'pending' ? errorMessage : item.error,
        })),
      }))
      setAbortController(null)
    }
  }, [])

  const cancelDownload = useCallback(() => {
    downloadTriggeredRef.current = false
    setPollingData(null)
    if (abortController) {
      abortController.abort()
      setAbortController(null)
    }
    setState(initialState)
  }, [abortController])

  const clearDownload = useCallback(() => {
    downloadTriggeredRef.current = false
    setPollingData(null)
    setState(initialState)
  }, [])

  return (
    <DownloadContext.Provider
      value={{
        state,
        startDownload,
        cancelDownload,
        clearDownload,
        isVisible,
      }}
    >
      {children}
    </DownloadContext.Provider>
  )
}

export function useDownload(): DownloadContextType {
  const context = useContext(DownloadContext)
  if (context === undefined) {
    throw new Error('useDownload must be used within a DownloadProvider')
  }
  return context
}
